/**
 * Event Radar — Event Intelligence
 *
 * Monitors upcoming business events across Africa.
 * Scores relevance, recommends posture, captures post-event contacts.
 */

import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Calendar, MapPin, Users, Target, TrendingUp, ExternalLink,
  Plus, ChevronRight, Star, Mic2, Eye, Send, X, Clock,
  BarChart3, Globe, Filter, RefreshCw, CheckCircle2,
  UserPlus, FileText, MessageCircle, Zap, Building2, Trash2, AlertTriangle,
} from "lucide-react";

const POSTURE_CONFIG = {
  speak:         { label: "Speak",         color: "bg-violet-100 text-violet-700 border-violet-300" },
  attend:        { label: "Attend",        color: "bg-blue-100 text-blue-700 border-blue-300" },
  sponsor:       { label: "Sponsor",       color: "bg-amber-100 text-amber-700 border-amber-300" },
  outreach_only: { label: "Outreach Only", color: "bg-teal-100 text-teal-700 border-teal-300" },
  monitor:       { label: "Monitor",       color: "bg-slate-100 text-slate-600 border-slate-300" },
  skip:          { label: "Skip",          color: "bg-slate-200 text-slate-700 border-slate-400" },
};

const TIER_BADGES = {
  "c-suite": { label: "C-Suite", color: "bg-amber-50 text-amber-700 border-amber-300" },
  senior:    { label: "Senior",  color: "bg-blue-50 text-blue-600 border-blue-200" },
  general:   { label: "General", color: "bg-slate-50 text-slate-500 border-slate-200" },
};

export default function EventRadar() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showCapture, setShowCapture] = useState(null);
  const [filterCountry, setFilterCountry] = useState("");
  const [filterPosture, setFilterPosture] = useState("");
  const [minRelevance, setMinRelevance] = useState(0);
  const [showReset, setShowReset] = useState(false);  // N4 Commit B — destructive reset confirm

  // ─── Data Fetching ──────────────────────────────────────────

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filterCountry) params.set("country", filterCountry);
      if (filterPosture) params.set("posture", filterPosture);
      if (minRelevance > 0) params.set("min_relevance", minRelevance.toString());

      const [eventsRes, statsRes] = await Promise.all([
        fetch(`${API}/events?${params}`, { credentials: "include" }),
        fetch(`${API}/events/stats`, { credentials: "include" }),
      ]);

      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data.events || []);
      }
      if (statsRes.ok) setStats(await statsRes.json());
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [filterCountry, filterPosture, minRelevance]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleScan = async () => {
    setScanning(true);
    try {
      // N4 Commit B — a scan NEVER wipes. run_event_scan adds unseen events and skips known
      // ones (their our_posture / posture_set_at / contacts_captured are left untouched).
      const res = await fetch(`${API}/events/scan`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "started") {
          toast.success("Scanning real web sources for verified events. This may take a minute...");
          setTimeout(fetchEvents, 20000);
          setTimeout(fetchEvents, 40000);
          setTimeout(fetchEvents, 70000);
          setTimeout(fetchEvents, 100000);
        } else if (data.status === "already_running") {
          toast.info("A scan is already in progress");
          setTimeout(fetchEvents, 20000);
        }
      }
    } catch {
      toast.error("Failed to start event scan");
    } finally {
      setScanning(false);
    }
  };

  // N4 Commit B — the destructive reset is now a separate, clearly-labelled control.
  const handleReset = async () => {
    try {
      const res = await fetch(`${API}/events/wipe-all`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Reset complete — ${d.deleted} event(s) removed. Run a scan to repopulate.`);
        setEvents([]);
        setSelectedEvent(null);
        setShowReset(false);
        fetchEvents();
      } else {
        toast.error("Reset failed");
      }
    } catch {
      toast.error("Reset failed");
    }
  };

  const handleSetPosture = async (eventId, posture) => {
    // N3 Commit D — optimistic: a skipped event leaves the screen at once (like tenders on this page),
    // then the refetch confirms. It is only removed from the DEFAULT view; the Skip posture filter still shows it.
    const optimisticSkip = posture === "skip" && filterPosture !== "skip";
    if (optimisticSkip) setEvents((prev) => prev.filter((e) => e.event_id !== eventId));
    try {
      const res = await fetch(`${API}/events/${eventId}/posture`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ posture }),
      });
      if (res.ok) {
        toast.success(`Posture set: ${POSTURE_CONFIG[posture]?.label}`);
        fetchEvents();
        if (selectedEvent?.event_id === eventId) {
          const updated = await fetch(`${API}/events/${eventId}`, { credentials: "include" });
          if (updated.ok) setSelectedEvent(await updated.json());
        }
      } else if (optimisticSkip) {
        fetchEvents();
      }
    } catch {
      toast.error("Failed to set posture");
      if (optimisticSkip) fetchEvents();
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return d; }
  };

  const daysUntil = (d) => {
    if (!d) return null;
    const diff = Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const uniqueCountries = [...new Set(events.map(e => e.country).filter(Boolean))].sort();
  // N3 Commit D — a skipped event leaves the DEFAULT list; it returns only when the posture
  // filter is explicitly set to "skip". Nothing is deleted and nothing is hidden for good.
  const displayEvents = filterPosture ? events : events.filter((e) => e.our_posture !== "skip");

  // ─── Render ─────────────────────────────────────────────────

  return (
    <div className="p-6" data-testid="event-radar-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Globe className="w-5 h-5 text-blue-600" />
            <h1 className="text-xl font-bold tracking-tight text-slate-800">Event Radar</h1>
            <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-600 ml-1">INTELLIGENCE</Badge>
          </div>
          <p className="text-sm text-slate-500">
            Pan-African event monitoring — discover, score, and act on business events
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-slate-300" onClick={handleScan} disabled={scanning} data-testid="scan-events-btn">
            {scanning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            {events.length === 0 ? "Scan for Events" : "Scan for New Events"}
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowAdd(true)} data-testid="add-event-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Event
          </Button>
          <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setShowReset(true)} data-testid="reset-events-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Reset all
          </Button>
        </div>
      </div>

      {/* N4 Commit B — destructive reset: names exactly what will be lost before wiping */}
      <Dialog open={showReset} onOpenChange={setShowReset}>
        <DialogContent className="max-w-md" data-testid="reset-events-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Reset all events?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>This permanently deletes every stored event and cannot be undone. A scan afterwards re-discovers events but <strong>cannot</strong> restore your decisions or contacts.</p>
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1" data-testid="reset-loss-summary">
              <p><strong data-testid="reset-count-events">{stats?.total_events ?? 0}</strong> events will be deleted</p>
              <p><strong data-testid="reset-count-postures">{stats ? ((stats.total_events ?? 0) - (stats.by_posture?.undecided ?? 0)) : 0}</strong> posture decisions will be lost</p>
              <p><strong data-testid="reset-count-contacts">{stats?.contacts_captured ?? 0}</strong> captured contacts will be lost</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReset(false)} data-testid="reset-cancel-btn">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleReset} data-testid="reset-confirm-btn">Delete everything</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="event-stats">
          <StatCard icon={<Calendar className="w-4 h-4 text-blue-600" />} label="Upcoming" value={stats.upcoming} />
          <StatCard icon={<Target className="w-4 h-4 text-emerald-600" />} label="Attending / Speaking" value={stats.attending} />
          <StatCard icon={<UserPlus className="w-4 h-4 text-violet-600" />} label="Contacts Captured" value={stats.contacts_captured} />
          <StatCard icon={<BarChart3 className="w-4 h-4 text-amber-600" />} label="Total Tracked" value={stats.total_events} />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="event-filters">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <Filter className="w-3.5 h-3.5" /> Filters:
        </div>
        <Select value={filterCountry} onValueChange={(v) => setFilterCountry(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-country">
            <SelectValue placeholder="All Markets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Markets</SelectItem>
            {uniqueCountries.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPosture} onValueChange={(v) => setFilterPosture(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-posture">
            <SelectValue placeholder="All Postures" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Postures</SelectItem>
            {Object.entries(POSTURE_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={minRelevance.toString()} onValueChange={(v) => setMinRelevance(parseInt(v))}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-relevance">
            <SelectValue placeholder="Min Relevance" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Any Relevance</SelectItem>
            <SelectItem value="25">25+ Relevance</SelectItem>
            <SelectItem value="50">50+ Relevance</SelectItem>
            <SelectItem value="70">70+ High Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Event List + Detail Split View */}
      <div className="flex gap-4">
        {/* Event List */}
        <div className={`${selectedEvent ? "w-1/2" : "w-full"} transition-all`}>
          {displayEvents.length === 0 && !loading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-12 text-center shadow-sm">
              <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-base font-semibold text-slate-700 mb-2">No Events Found</h3>
              <p className="text-sm text-slate-500 mb-4">
                Click "Scan for Events" to search for upcoming African business events
              </p>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleScan} disabled={scanning}>
                <Zap className="w-4 h-4 mr-2" /> Scan for Events
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto pr-1" data-testid="event-list">
              {displayEvents.map((event) => (
                <EventCard
                  key={event.event_id}
                  event={event}
                  isSelected={selectedEvent?.event_id === event.event_id}
                  onClick={() => setSelectedEvent(event)}
                  onSetPosture={handleSetPosture}
                  formatDate={formatDate}
                  daysUntil={daysUntil}
                />
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedEvent && (
          <div className="w-1/2 sticky top-0">
            <EventDetail
              event={selectedEvent}
              onClose={() => setSelectedEvent(null)}
              onSetPosture={handleSetPosture}
              onCapture={() => setShowCapture(selectedEvent.event_id)}
              formatDate={formatDate}
              daysUntil={daysUntil}
              onRefresh={fetchEvents}
            />
          </div>
        )}
      </div>

      {/* Add Event Modal */}
      {showAdd && (
        <AddEventModal
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); fetchEvents(); }}
        />
      )}

      {/* Capture Contact Modal */}
      {showCapture && (
        <CaptureContactModal
          eventId={showCapture}
          onClose={() => setShowCapture(null)}
          onCaptured={() => { setShowCapture(null); fetchEvents(); }}
        />
      )}
    </div>
  );
}

// ─── Event Card ─────────────────────────────────────────────────

function EventCard({ event, isSelected, onClick, formatDate, daysUntil }) {
  const days = daysUntil(event.date_start);
  const isPast = days !== null && days < 0;
  const relevance = event.relevance?.score || 0;
  const posture = event.our_posture;
  const postureConfig = POSTURE_CONFIG[posture];
  const tierConfig = TIER_BADGES[event.tier] || TIER_BADGES.general;

  return (
    <div
      className={`rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${
        isSelected
          ? "border-blue-400 bg-blue-50/50 shadow-md"
          : "border-slate-200 bg-white shadow-sm hover:border-slate-300"
      }`}
      onClick={onClick}
      data-testid={`event-card-${event.event_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0 mr-3">
          <h3 className={`text-sm font-semibold ${isPast ? "text-slate-400" : "text-slate-800"} line-clamp-1`}>
            {event.name}
          </h3>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="flex items-center gap-1 text-[11px] text-slate-500">
              <Calendar className="w-3 h-3" /> {formatDate(event.date_start)}
            </span>
            <span className="flex items-center gap-1 text-[11px] text-slate-500">
              <MapPin className="w-3 h-3" /> {event.city || event.country}
            </span>
            {event.estimated_attendees && (
              <span className="flex items-center gap-1 text-[11px] text-slate-500">
                <Users className="w-3 h-3" /> {event.estimated_attendees.toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <RelevanceScore score={relevance} />
          {days !== null && days >= 0 && days <= 30 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">
              {days === 0 ? "TODAY" : `${days}d away`}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge variant="outline" className={`text-[9px] border ${tierConfig.color}`}>{tierConfig.label}</Badge>
        <Badge variant="outline" className="text-[9px] capitalize">{event.event_type}</Badge>
        <Badge variant="outline" className="text-[9px]">{event.country}</Badge>
        {event.source === "verified" && (
          <Badge variant="outline" className="text-[9px] border-green-300 text-green-700 bg-green-50">
            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Verified
          </Badge>
        )}
        {posture && postureConfig && (
          <Badge variant="outline" className={`text-[9px] border ${postureConfig.color}`}>
            {postureConfig.label}
          </Badge>
        )}
        {event.speaking_opportunity && (
          <Badge variant="outline" className="text-[9px] border-violet-300 text-violet-600">
            <Mic2 className="w-2.5 h-2.5 mr-0.5" /> Speaker Opp
          </Badge>
        )}
        {(event.relevance?.target_accounts_present?.length || 0) > 0 && (
          <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-600">
            <Target className="w-2.5 h-2.5 mr-0.5" /> {event.relevance.target_accounts_present.length} targets
          </Badge>
        )}
      </div>
    </div>
  );
}

// ─── Event Detail Panel ─────────────────────────────────────────

function EventDetail({ event, onClose, onSetPosture, onCapture, formatDate, daysUntil, onRefresh }) {
  const relevance = event.relevance || {};
  const recommended = event.recommended_posture || {};
  const days = daysUntil(event.date_start);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm max-h-[calc(100vh-280px)] overflow-y-auto" data-testid="event-detail">
      {/* Header */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-base font-bold text-slate-800 leading-snug pr-4">{event.name}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3 line-clamp-3">{event.description}</p>
        <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600">
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-blue-500" /> {formatDate(event.date_start)} — {formatDate(event.date_end)}</span>
          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-blue-500" /> {event.location || event.city}</span>
          {event.url && (
            <a href={event.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
              <ExternalLink className="w-3.5 h-3.5" /> Website
            </a>
          )}
        </div>
        {days !== null && days >= 0 && (
          <div className={`mt-2 inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${
            days <= 14 ? "bg-red-100 text-red-700" : days <= 30 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
          }`}>
            <Clock className="w-3 h-3" />
            {days === 0 ? "Happening today" : `${days} days away`}
          </div>
        )}
        {event.source === "verified" && (
          <div className="mt-2 ml-2 inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
            <CheckCircle2 className="w-3 h-3" /> Date verified from official source
          </div>
        )}
      </div>

      {/* Relevance */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Relevance Analysis</h3>
          <RelevanceScore score={relevance.score || 0} size="md" />
        </div>
        {relevance.reasons?.length > 0 && (
          <ul className="space-y-1 mb-3">
            {relevance.reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                {r}
              </li>
            ))}
          </ul>
        )}
        {(relevance.target_accounts_present?.length || 0) > 0 && (
          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Target Accounts Present</p>
            <div className="flex gap-1.5 flex-wrap">
              {relevance.target_accounts_present.map((a) => (
                <Badge key={a} variant="outline" className="text-[9px] border-emerald-300 text-emerald-700 bg-white">{a}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Rex Recommendation */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-4 h-4 text-amber-500" />
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Rex Recommends</h3>
        </div>
        {recommended.recommended && (
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1.5">
              <Badge className={`text-xs ${POSTURE_CONFIG[recommended.recommended]?.color || "bg-slate-100 text-slate-600"}`}>
                {POSTURE_CONFIG[recommended.recommended]?.label || recommended.recommended}
              </Badge>
            </div>
            <p className="text-xs text-slate-600 mb-2">{recommended.reason}</p>
            {recommended.actions?.length > 0 && (
              <div className="space-y-1">
                {recommended.actions.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <ChevronRight className="w-3 h-3 text-blue-400" /> {a}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Set Posture */}
      <div className="p-5 border-b border-slate-100">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Your Decision</h3>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(POSTURE_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => onSetPosture(event.event_id, key)}
              className={`px-3 py-2 rounded-lg text-[11px] font-medium border transition-all hover:shadow-sm ${
                event.our_posture === key
                  ? `${cfg.color} ring-2 ring-offset-1 ring-blue-400`
                  : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
              data-testid={`posture-${key}`}
            >
              {cfg.label}
            </button>
          ))}
        </div>
      </div>

      {/* Event Details */}
      <div className="p-5 border-b border-slate-100">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Details</h3>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <DetailRow label="Type" value={event.event_type} />
          <DetailRow label="Tier" value={event.tier} />
          <DetailRow label="Scope" value={event.scope} />
          <DetailRow label="Expected Attendees" value={event.estimated_attendees?.toLocaleString()} />
          <DetailRow label="Speaking Opp" value={event.speaking_opportunity ? "Yes" : "No"} />
          <DetailRow label="Recurring" value={event.recurring || "Unknown"} />
        </div>
        {(event.sponsors?.length > 0 || event.known_attendees?.length > 0) && (
          <div className="mt-3 space-y-2">
            {event.sponsors?.length > 0 && (
              <div>
                <p className="text-[10px] text-slate-500 uppercase mb-1">Sponsors</p>
                <div className="flex gap-1 flex-wrap">
                  {event.sponsors.map((s) => <Badge key={s} variant="outline" className="text-[9px]">{s}</Badge>)}
                </div>
              </div>
            )}
            {event.known_attendees?.length > 0 && (
              <div>
                <p className="text-[10px] text-slate-500 uppercase mb-1">Known Attendees</p>
                <div className="flex gap-1 flex-wrap">
                  {event.known_attendees.map((a) => <Badge key={a} variant="outline" className="text-[9px]">{a}</Badge>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post-Event Actions */}
      <div className="p-5">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Post-Event</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="text-xs" onClick={onCapture} data-testid="capture-contact-btn">
            <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Capture Contact
          </Button>
        </div>
        {event.contacts_captured?.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-[10px] text-slate-500 uppercase">Captured Contacts ({event.contacts_captured.length})</p>
            {event.contacts_captured.map((c) => (
              <div key={c.contact_id} className="flex items-center gap-2 rounded-lg bg-slate-50 p-2 border border-slate-200 text-xs">
                <UserPlus className="w-3 h-3 text-blue-500 shrink-0" />
                <div>
                  <span className="font-medium text-slate-700">{c.name}</span>
                  {c.company && <span className="text-slate-500"> — {c.company}</span>}
                  {c.title && <span className="text-slate-400"> ({c.title})</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add Event Modal ────────────────────────────────────────────

function AddEventModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", description: "", date_start: "", date_end: "",
    location: "", city: "", country: "", scope: "National",
    event_type: "conference", tier: "senior", url: "",
    speaking_opportunity: false, themes: "", sectors: "",
    sponsors: "", known_attendees: "", estimated_attendees: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.date_start || !form.country) {
      toast.error("Name, date, and country are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        date_start: new Date(form.date_start).toISOString(),
        date_end: form.date_end ? new Date(form.date_end).toISOString() : new Date(form.date_start).toISOString(),
        themes: form.themes ? form.themes.split(",").map(s => s.trim()) : [],
        sectors: form.sectors ? form.sectors.split(",").map(s => s.trim()) : [],
        sponsors: form.sponsors ? form.sponsors.split(",").map(s => s.trim()) : [],
        known_attendees: form.known_attendees ? form.known_attendees.split(",").map(s => s.trim()) : [],
        estimated_attendees: form.estimated_attendees ? parseInt(form.estimated_attendees) : null,
      };
      const res = await fetch(`${API}/events/create`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Event added to radar");
        onCreated();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add event");
      }
    } catch {
      toast.error("Failed to add event");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-event-modal">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-800">Add Event</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <FormField label="Event Name *" value={form.name} onChange={v => setField("name", v)} />
          <FormField label="Description" value={form.description} onChange={v => setField("description", v)} multiline />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Start Date *" value={form.date_start} onChange={v => setField("date_start", v)} type="date" />
            <FormField label="End Date" value={form.date_end} onChange={v => setField("date_end", v)} type="date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="City" value={form.city} onChange={v => setField("city", v)} />
            <FormField label="Country *" value={form.country} onChange={v => setField("country", v)} placeholder="e.g. Nigeria" />
          </div>
          <FormField label="Location / Venue" value={form.location} onChange={v => setField("location", v)} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-medium mb-1 block">Event Type</label>
              <Select value={form.event_type} onValueChange={v => setField("event_type", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["conference", "summit", "forum", "workshop", "awards", "networking", "exhibition"].map(t => (
                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-medium mb-1 block">Tier</label>
              <Select value={form.tier} onValueChange={v => setField("tier", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="c-suite">C-Suite</SelectItem>
                  <SelectItem value="senior">Senior</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <FormField label="Themes (comma-separated)" value={form.themes} onChange={v => setField("themes", v)} placeholder="leadership, talent, digital" />
          <FormField label="Known Attendees (comma-separated)" value={form.known_attendees} onChange={v => setField("known_attendees", v)} placeholder="MTN, Dangote, Access Bank" />
          <FormField label="Website URL" value={form.url} onChange={v => setField("url", v)} placeholder="https://..." />
          <FormField label="Expected Attendees" value={form.estimated_attendees} onChange={v => setField("estimated_attendees", v)} placeholder="e.g. 500" />
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={form.speaking_opportunity} onChange={e => setField("speaking_opportunity", e.target.checked)} className="rounded border-slate-300" />
            Speaking opportunity available
          </label>
        </div>
        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSubmit} disabled={saving} data-testid="save-event-btn">
            {saving ? "Adding..." : "Add to Radar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Capture Contact Modal ──────────────────────────────────────

function CaptureContactModal({ eventId, onClose, onCaptured }) {
  const [form, setForm] = useState({
    name: "", title: "", company: "", email: "",
    phone: "", linkedin_url: "", conversation_notes: "",
    signal_strength: "medium", follow_up_action: "",
    create_brief: false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.name) { toast.error("Contact name is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/events/${eventId}/capture`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Contact captured${data.brief_created ? " + Brief created" : ""}`);
        onCaptured();
      }
    } catch {
      toast.error("Failed to capture contact");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="capture-contact-modal">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-800">Capture Contact</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <FormField label="Name *" value={form.name} onChange={v => setField("name", v)} />
          <FormField label="Title / Role" value={form.title} onChange={v => setField("title", v)} placeholder="e.g. CHRO" />
          <FormField label="Company" value={form.company} onChange={v => setField("company", v)} placeholder="e.g. MTN Nigeria" />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Email" value={form.email} onChange={v => setField("email", v)} />
            <FormField label="Phone" value={form.phone} onChange={v => setField("phone", v)} />
          </div>
          <FormField label="LinkedIn URL" value={form.linkedin_url} onChange={v => setField("linkedin_url", v)} placeholder="https://linkedin.com/in/..." />
          <FormField label="Conversation Notes" value={form.conversation_notes} onChange={v => setField("conversation_notes", v)} multiline placeholder="What did you discuss? Any signals?" />
          <FormField label="Follow-up Action" value={form.follow_up_action} onChange={v => setField("follow_up_action", v)} placeholder="e.g. Send CulturePulse deck" />
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-medium mb-1 block">Signal Strength</label>
            <Select value={form.signal_strength} onValueChange={v => setField("signal_strength", v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High — Active interest</SelectItem>
                <SelectItem value="medium">Medium — Warm conversation</SelectItem>
                <SelectItem value="low">Low — Exchanged cards</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={form.create_brief} onChange={e => setField("create_brief", e.target.checked)} className="rounded border-slate-300" />
            Auto-create Commercial Brief for this contact
          </label>
        </div>
        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSubmit} disabled={saving} data-testid="save-contact-btn">
            {saving ? "Saving..." : "Capture Contact"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared Sub-Components ──────────────────────────────────────

function RelevanceScore({ score, size = "sm" }) {
  const color = score >= 70 ? "text-emerald-700 bg-emerald-100" : score >= 40 ? "text-amber-700 bg-amber-100" : "text-slate-600 bg-slate-100";
  const s = size === "md" ? "text-sm px-2.5 py-1" : "text-[10px] px-2 py-0.5";
  return <span className={`${s} rounded-full font-bold ${color}`} data-testid="relevance-score">{score}</span>;
}

function StatCard({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">{icon}</div>
      <p className="text-xl font-bold text-slate-800">{value ?? 0}</p>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase">{label}</p>
      <p className="text-xs text-slate-700 capitalize">{value || "-"}</p>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", multiline = false, placeholder = "" }) {
  return (
    <div>
      <label className="text-[10px] uppercase text-slate-500 font-medium mb-1 block">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
        />
      ) : (
        <Input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-8 text-xs"
        />
      )}
    </div>
  );
}
